import { useState } from "react";
import { Filter, Search, Check, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

interface SkillsFilterProps {
  skillsFilter: string[];
  setSkillsFilter: (skills: string[]) => void;
  skillsOperator: "AND" | "OR";
  setSkillsOperator: (operator: "AND" | "OR") => void;
  allSkills: string[];
  onOpen?: () => void;
}

export const SkillsFilter = ({
  skillsFilter,
  setSkillsFilter,
  skillsOperator,
  setSkillsOperator,
  allSkills,
  onOpen
}: SkillsFilterProps) => {
  const [skillsMenuOpen, setSkillsMenuOpen] = useState(false);
  const [skillsSearchTerm, setSkillsSearchTerm] = useState("");
  const { t } = useTranslation();

  const handleOpenChange = (open: boolean) => {
    setSkillsMenuOpen(open);
    if (open) onOpen?.();
  };

  // Toggle skill selection
  const toggleSkill = (skill: string) => {
    if (skillsFilter.includes(skill)) {
      setSkillsFilter(skillsFilter.filter(s => s !== skill));
    } else {
      setSkillsFilter([...skillsFilter, skill]);
    }
  };

  // Filter skills based on search term - case insensitive
  const filteredSkills = allSkills.filter(skill => 
    skill.toLowerCase().includes(skillsSearchTerm.toLowerCase())
  );

  return (
    <DropdownMenu open={skillsMenuOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          className="flex items-center gap-2"
          aria-expanded={skillsMenuOpen}
          data-state={skillsMenuOpen ? "open" : "closed"}
        >
          <Filter className="h-4 w-4" />
          <span>{t("candidates.filters.skillsButton")}</span>
          {skillsFilter.length > 0 && (
            <span className="ml-1 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 text-xs font-medium px-2 py-0.5 rounded-sm">
              {skillsFilter.length}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        className="w-64 p-3 bg-white dark:bg-gray-900"
        align="start"
        onInteractOutside={() => setSkillsMenuOpen(false)}
      >
        <div className="space-y-3">
          {/* Operator Toggle */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("candidates.filters.operator.match")}</span>
            <ToggleGroup 
              type="single" 
              value={skillsOperator} 
              onValueChange={(value) => value && setSkillsOperator(value as "AND" | "OR")}
              className="h-8 bg-gray-50 dark:bg-gray-800 rounded-sm p-1"
            >
              <ToggleGroupItem 
                value="AND" 
                aria-label="All skills (AND)" 
                className="text-xs px-3 data-[state=on]:bg-blue-100 data-[state=on]:text-blue-700 data-[state=on]:border-blue-200"
              >
                <Check className="h-3 w-3 mr-1" />
                {t("candidates.filters.operator.all")}
              </ToggleGroupItem>
              <ToggleGroupItem 
                value="OR" 
                aria-label="Any skills (OR)" 
                className="text-xs px-3 data-[state=on]:bg-blue-100 data-[state=on]:text-blue-700 data-[state=on]:border-blue-200"
              >
                <Plus className="h-3 w-3 mr-1" />
                {t("candidates.filters.operator.any")}
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
          
          <div className="relative">
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder={t("candidates.filters.skillsSearchPlaceholder")}
              value={skillsSearchTerm}
              onChange={(e) => setSkillsSearchTerm(e.target.value)}
              className="pl-8 text-sm h-8"
            />
          </div>
          
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {filteredSkills.length === 0 ? (
              <p className="text-sm text-gray-500 py-1">{t("candidates.filters.noSkillsMatch")}</p>
            ) : (
              filteredSkills.map(skill => (
                <div key={skill} className="flex items-center space-x-2">
                  <Checkbox 
                    id={`skill-${skill}`} 
                    checked={skillsFilter.includes(skill)}
                    onCheckedChange={() => toggleSkill(skill)}
                  />
                  <Label 
                    htmlFor={`skill-${skill}`}
                    className="text-sm font-normal cursor-pointer flex-1"
                  >
                    {skill}
                  </Label>
                </div>
              ))
            )}
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
